import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Users, Plus, UserPlus } from 'lucide-react';
import { AppContext } from '../../../context/AppContext';
import {
  PostCallWithErrorResponse,
  simpleGetCallWithErrorResponse,
} from '../../../api/ApiServices';
import {
  Button,
  Table,
  Badge,
  Pagination,
  Modal,
  MultiSelect,
} from '../../../components/ui';
import { ToastMsg } from '../../TostMsg';
import styles from './PushCampaigns.module.scss';

const ROLE = 'CRM Admin';
const PLATFORM = 'Push';
const USER_TYPE = 'Customer';
const SEARCH_DEBOUNCE_MS = 300;

const formatDate = (val) => {
  if (!val) return '';
  const d = new Date(val);
  if (isNaN(d.getTime())) return '';
  return d.toLocaleDateString(undefined, {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
};

const toUserOption = (u) => {
  const id = u.id ?? u.user_id ?? u.userId;
  const name =
    u.name ||
    u.full_name ||
    u.fullName ||
    [u.first_name, u.last_name].filter(Boolean).join(' ').trim() ||
    u.email ||
    u.phone ||
    `User #${id}`;
  const sublabel = u.email || u.phone || u.mobile || '';
  return { value: id, label: name, sublabel };
};

export default function PushGroupsList() {
  const { sidebar, ApiConfigg } = useContext(AppContext);
  const navigate = useNavigate();

  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [meta, setMeta] = useState({
    total: 0,
    numPages: 1,
    hasNext: false,
    hasPrevious: false,
  });
  const pageSize = 10;

  const [modalGroup, setModalGroup] = useState(null);
  const [modalMembers, setModalMembers] = useState([]);
  const [modalSubmitting, setModalSubmitting] = useState(false);
  const [users, setUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [userSearch, setUserSearch] = useState('');
  const [selectedUsersCache, setSelectedUsersCache] = useState({});
  const debounceRef = useRef(null);

  const fetchGroups = (pageNum = page) => {
    const url = ApiConfigg?.GET_PROMOTION_GROUPS;
    if (!url) return;
    const qs = [
      `platform=${encodeURIComponent(PLATFORM)}`,
      `page=${pageNum}`,
      `page_size=${pageSize}`,
      `role=${encodeURIComponent(ROLE)}`,
    ].join('&');
    setLoading(true);
    simpleGetCallWithErrorResponse(`${url}?${qs}`)
      .then((res) => {
        const json = res?.json;
        const list = Array.isArray(json?.data) ? json.data : [];
        setGroups(list);
        setMeta({
          total: Number(json?.total) || 0,
          numPages: Number(json?.num_pages) || 1,
          hasNext: !!json?.has_next,
          hasPrevious: !!json?.has_previous,
        });
      })
      .catch((err) => console.error('Push groups fetch error:', err))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchGroups(page);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ApiConfigg?.GET_PROMOTION_GROUPS, page]);

  const fetchUsers = (q, groupId = modalGroup?.id) => {
    const url = ApiConfigg?.GET_USER_LIST;
    if (!url) return;
    const parts = [
      `search=${encodeURIComponent(q || '')}`,
      `type=${encodeURIComponent(USER_TYPE)}`,
      `role=${ROLE}`,
    ];
    if (groupId != null) parts.push(`groupId=${encodeURIComponent(groupId)}`);
    const qs = parts.join('&');
    setUsersLoading(true);
    simpleGetCallWithErrorResponse(`${url}?${qs}`)
      .then((res) => {
        const json = res?.json;
        const list = Array.isArray(json?.data)
          ? json.data
          : Array.isArray(json?.data?.users)
          ? json.data.users
          : Array.isArray(json) ? json : [];
        setUsers(list.map(toUserOption).filter((o) => o.value != null));
      })
      .catch((err) => console.error('User list fetch error:', err))
      .finally(() => setUsersLoading(false));
  };

  useEffect(() => {
    if (!modalGroup) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => fetchUsers(userSearch), SEARCH_DEBOUNCE_MS);
    return () => debounceRef.current && clearTimeout(debounceRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userSearch, modalGroup]);

  const openAddMembers = (group) => {
    setModalGroup(group);
    setModalMembers([]);
    setUserSearch('');
    setSelectedUsersCache({});
    fetchUsers('', group?.id);
  };

  const closeAddMembers = () => {
    if (modalSubmitting) return;
    setModalGroup(null);
    setModalMembers([]);
    setUserSearch('');
  };

  const memberOptions = useMemo(() => {
    const seen = new Set(users.map((u) => String(u.value)));
    const cached = Object.values(selectedUsersCache).filter(
      (u) => u && !seen.has(String(u.value))
    );
    return [...users, ...cached];
  }, [users, selectedUsersCache]);

  const handleMembersChange = (next) => {
    setSelectedUsersCache((prev) => {
      const updated = { ...prev };
      next.forEach((id) => {
        if (!updated[id]) {
          const found = users.find((u) => String(u.value) === String(id));
          if (found) updated[id] = found;
        }
      });
      return updated;
    });
    setModalMembers(next);
  };

  const handleSubmitMembers = () => {
    if (!modalGroup) return;
    if (modalMembers.length === 0) {
      ToastMsg('error', 'Select at least one member to add.');
      return;
    }
    const builder = ApiConfigg?.POST_ADD_MEMBERS_TO_GROUP;
    if (typeof builder !== 'function') {
      ToastMsg('error', 'Add-members endpoint is not configured.');
      return;
    }
    const url = builder(modalGroup.id);
    const payload = {
      groupMembers: modalMembers.map((id) =>
        Number.isFinite(Number(id)) ? Number(id) : id
      ),
    };

    setModalSubmitting(true);
    PostCallWithErrorResponse(url, JSON.stringify(payload))
      .then((res) => {
        if (!res?.error) {
          ToastMsg(
            'success',
            res?.json?.message ||
              `${modalMembers.length} member${modalMembers.length === 1 ? '' : 's'} added to ${modalGroup.group_name}.`
          );
          setModalGroup(null);
          setModalMembers([]);
          setUserSearch('');
          fetchGroups(page);
        } else {
          ToastMsg('error', res?.json?.message || 'Failed to add members.');
        }
      })
      .catch((err) => {
        console.error('Add members error:', err);
        ToastMsg('error', 'Network error. Please try again.');
      })
      .finally(() => setModalSubmitting(false));
  };

  return (
    <div className={sidebar ? 'taskMain' : 'cx-active taskMain'} id="cx-main">
      <div className={styles.page}>
        <div className={styles.header}>
          <div className={styles.headerLeft}>
            <span className={styles.iconBadge}>
              <Users size={20} />
            </span>
            <div className={styles.titleBlock}>
              <h1>Push Groups</h1>
              <p>Manage audience groups used to target push notification campaigns.</p>
            </div>
          </div>
          <div className={styles.headerActions}>
            <Button
              variant="primary"
              leftIcon={<Plus size={16} />}
              onClick={() => navigate('/PushGroups/create')}
            >
              Create Group
            </Button>
          </div>
        </div>

        <div className={styles.tableWrap}>
          <div className={styles.tableScroll}>
            <Table hoverable>
              <thead>
                <tr>
                  <th>Group name</th>
                  <th>Description</th>
                  <th>Type</th>
                  <th>Platform</th>
                  <th>Members</th>
                  <th>Created</th>
                  <th>Status</th>
                  <th style={{ width: 160, textAlign: 'right' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={8} className={styles.loadingRow}>
                      Loading groups…
                    </td>
                  </tr>
                ) : groups.length === 0 ? (
                  <tr>
                    <td colSpan={8}>
                      <div className={styles.empty}>
                        <div className={styles.emptyTitle}>No groups yet</div>
                        <div>Create your first audience group to start targeting campaigns.</div>
                      </div>
                    </td>
                  </tr>
                ) : (
                  groups.map((g) => {
                    const id = g.id;
                    const name = g.group_name || '—';
                    const desc = g.group_description || '';
                    const type = g.group_type || '—';
                    const platform = g.group_platform || PLATFORM;
                    const memberCount = Number(g.user_count ?? 0);
                    const status = g.group_status || 'Active';
                    const created = formatDate(g.group_created_at);
                    return (
                      <tr key={id}>
                        <td>
                          <div className={styles.cellTitle}>{name}</div>
                          <div className={styles.cellMeta}>ID: {id}</div>
                        </td>
                        <td>
                          <div className={styles.descCell}>
                            {desc || <span className={styles.muted}>—</span>}
                          </div>
                        </td>
                        <td>{type}</td>
                        <td>{platform}</td>
                        <td>{memberCount}</td>
                        <td>{created || <span className={styles.muted}>—</span>}</td>
                        <td>
                          <Badge
                            variant={
                              String(status).toLowerCase() === 'inactive'
                                ? 'neutral'
                                : 'success'
                            }
                          >
                            {status}
                          </Badge>
                        </td>
                        <td style={{ textAlign: 'right' }}>
                          <Button
                            variant="ghost"
                            size="sm"
                            leftIcon={<UserPlus size={14} />}
                            onClick={() => openAddMembers(g)}
                          >
                            Add members
                          </Button>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </Table>
          </div>
        </div>

        <Pagination
          page={page}
          pageCount={meta.numPages}
          total={meta.total}
          itemLabel="groups"
          onPageChange={(p) => setPage(p)}
        />
      </div>

      <Modal
        show={!!modalGroup}
        onHide={closeAddMembers}
        size="md"
        title={
          modalGroup
            ? `Add members to "${modalGroup.group_name}"`
            : 'Add members'
        }
        footer={
          <>
            <Button
              variant="ghost"
              onClick={closeAddMembers}
              disabled={modalSubmitting}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleSubmitMembers}
              loading={modalSubmitting}
              leftIcon={<UserPlus size={14} />}
            >
              Add {modalMembers.length > 0 ? `${modalMembers.length} ` : ''}
              {modalMembers.length === 1 ? 'member' : 'members'}
            </Button>
          </>
        }
      >
        <MultiSelect
          label="Members"
          placeholder="Search users by name, email, or phone"
          value={modalMembers}
          onChange={handleMembersChange}
          options={memberOptions}
          searchValue={userSearch}
          onSearchChange={setUserSearch}
          loading={usersLoading}
          emptyMessage={
            userSearch ? `No users match "${userSearch}"` : 'No users available'
          }
          loadingMessage="Loading users…"
          helperText={
            modalGroup
              ? `Currently ${Number(modalGroup.user_count ?? 0)} member${Number(modalGroup.user_count ?? 0) === 1 ? '' : 's'} in this group.`
              : ''
          }
        />
      </Modal>
    </div>
  );
}
